import * as Icon from 'base/ui/components/Icons';

import { DatePicker, Form, InputNumber, Radio, Spin, message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import BoxContainer from 'base/ui/components/BoxContainer';
import CustomBreadcrumb from 'base/ui/components/CustomBreadcrumb';
import CustomButton from 'base/ui/components/Button';
import CustomInput from 'base/ui/components/CustomInput';
import CustomSelect from 'base/ui/components/CustomSelect';
import { SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { observer } from 'mobx-react';
import CustomProTable from 'base/ui/components/CustomTable';
import { useTranslation } from 'react-i18next';
import { usePermission } from 'hooks/usePermission';
import {
  wasteGroupingEnvironmentPermitPresenter,
  wasteGroupingEnvironmentPermitStore,
  commonMasterDataPresenter,
  commonMasterDataStore,
} from 'stores/root_store';
import { WasteGroupingEnvironmentPermitModel } from 'services/RawData/wasteGroupingEnvironmentPermit_services';
import { customFilterOption } from 'utils/searchHelper';
import { DATE_FORMAT_DEFAULT } from 'utils/constants';
import dayjs from 'dayjs';
import { formatDecimalNumber, sFormatNumber } from 'utils/formatHelper';

const createWasteGroupingEnvironmentPermitDetailPage = () => {
  return observer(() => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Kiểm tra phân quyền
    if (id) {
      usePermission('EDIT');
    } else {
      usePermission('CREATE');
    }
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    let isSaveAndContinue = false;
    const [wasteGroupOptions, setWasteGroupOptions] = useState<{ label: string; value: string }[]>([]);
    const [unitOptions, setUnitOptions] = useState<{ label: string; value: string }[]>([]);
    const [historyData, setHistoryData] = useState<any[]>([]);

    const initialValues = useMemo(
      () => ({
        actived: true,
        isDisplayCodeInReport: true,
      }),
      [],
    );

    const loadWasteGroups = async () => {
      await commonMasterDataPresenter.getDropdownWasteGroup(commonMasterDataStore);
      const options =
        commonMasterDataStore.commonRespone.data?.map((item) => ({
          label: item.value,
          value: item.key,
        })) || [];
      setWasteGroupOptions(options);
    };

    const loadUnits = async () => {
      await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'Unit_CT');
      const options = commonMasterDataStore.commonRespone.data?.map((item) => ({
        label: item.value,
        value: item.key,
      }));
      if (options?.length) {
        setUnitOptions(options);
      }
    };

    const loadDetail = async (permitId: string) => {
      setLoading(true);
      await wasteGroupingEnvironmentPermitPresenter.getWasteGroupingEnvironmentPermitDetail(
        wasteGroupingEnvironmentPermitStore,
        permitId
      );
      if (wasteGroupingEnvironmentPermitStore.detailResponse.isSuccess && wasteGroupingEnvironmentPermitStore.detailResponse.data) {
        const detail = wasteGroupingEnvironmentPermitStore.detailResponse.data;
        form.setFieldsValue({
          wasteGroupingEnvironmentPermitCustomCode: detail.wasteGroupingEnvironmentPermitCustomCode,
          wasteGroupingEnvironmentPermitName: detail.wasteGroupingEnvironmentPermitName,
          wasteGroup: detail.wasteGroup,
          wasteGroupingContractId: detail.wasteGroupingContractId,
          quantity: detail.quantity,
          year: detail.year,
          effectiveDate: (detail as any).effectiveDate
            ? dayjs((detail as any).effectiveDate)
            : undefined,
          unit: detail.unit,
          isDisplayCodeInReport: detail.isDisplayCodeInReport,
          actived: detail.actived,
        });
        setHistoryData(detail.wasteGroupingEnvironmentPermitLogs || []);
      } else {
        navigate('/MasterData/');
        message.error(wasteGroupingEnvironmentPermitStore.detailResponse.message);
      }
      setLoading(false);
    };

    useEffect(() => {
      loadWasteGroups();
      loadUnits();
      if (id) {
        loadDetail(id);
      } else {
        form.setFieldsValue(initialValues);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleSubmit = async (values: WasteGroupingEnvironmentPermitModel & { effectiveDate?: any }) => {
      setLoading(true);
      const payload: WasteGroupingEnvironmentPermitModel = {
        ...values,
        effectiveDate: values.effectiveDate
          ? (values.effectiveDate as any).format('YYYY-MM-DD')
          : undefined,
      };
      if (id) {
        await wasteGroupingEnvironmentPermitPresenter.updateWasteGroupingEnvironmentPermit(
          wasteGroupingEnvironmentPermitStore,
          {
            ...payload,
            wasteGroupingEnvironmentPermitId: id,
          }
        );
      } else {
        await wasteGroupingEnvironmentPermitPresenter.createWasteGroupingEnvironmentPermit(
          wasteGroupingEnvironmentPermitStore,
          payload
        );
      }
      setLoading(false);

      if (wasteGroupingEnvironmentPermitStore.crudResponse.isSuccess) {
        message.success(wasteGroupingEnvironmentPermitStore.crudResponse.message);
        if (!isSaveAndContinue) {
          navigate('/MasterData/WasteGroupingEnvironmentPermit');
        } else if (!id) {
          form.resetFields();
          form.setFieldsValue(initialValues);
          // Reload dropdown options to ensure fresh data
          loadWasteGroups();
          loadUnits();
        } else if (id) {
          // Reload detail data including history when update and continue
          await loadDetail(id);
        }
      } else {
        message.error(wasteGroupingEnvironmentPermitStore.crudResponse.message);
      }
    };

    const ButtonBar = () => (
      <>
        <CustomButton
          icon={<Icon.Download className="!fill-[#262626]" />}
          htmlType="submit"
          loading={loading}
          disabled={loading}
          onClick={() => {
            isSaveAndContinue = false;
            form.submit();
          }}
        >
          {t('btn.save')}
        </CustomButton>
        <CustomButton
          type="primary"
          icon={<Icon.Download />}
          loading={loading}
          disabled={loading}
          onClick={() => {
            isSaveAndContinue = true;
            form.submit();
          }}
        >
          {id ? t('btn.save-update') : t('btn.save-continue-create')}
        </CustomButton>
      </>
    );

    return (
      <div className="flex flex-col h-full">
        <CustomBreadcrumb
          pagetitle={[
            {
              title: t('masterdata.waste-grouping-environment-permit.title'),
              link: '/MasterData/WasteGroupingEnvironmentPermit',
            },
            {
              title: id
                ? t('masterdata.waste-grouping-environment-permit.update-title')
                : t('masterdata.waste-grouping-environment-permit.create-title'),
              link: id ? `/MasterData/WasteGroupingEnvironmentPermit/${id}` : '/MasterData/WasteGroupingEnvironmentPermit/Create',
            },
          ]}
          extra={<div className="custom__mobile--save flex space-x-2"><ButtonBar /></div>}
        />
        <BoxContainer>
          <Spin spinning={loading}>
            <SearchForm
              column="col-2"
              form={form}
              initialValues={initialValues}
              className="gap-6"
              onFinish={handleSubmit}
            >
              <SearchItem
                name="wasteGroupingEnvironmentPermitCustomCode"
                label={t('masterdata.waste-grouping-environment-permit.code')}
                rules={[{ required: true, message: t('masterdata.waste-grouping-environment-permit.code-required') }]}
              >
                <CustomInput placeholder={t('masterdata.waste-grouping-environment-permit.code-placeholder')} />
              </SearchItem>
              <SearchItem
                name="wasteGroupingEnvironmentPermitName"
                label={t('masterdata.waste-grouping-environment-permit.name')}
                rules={[{ required: true, message: t('masterdata.waste-grouping-environment-permit.name-required') }]}
              >
                <CustomInput placeholder={t('masterdata.waste-grouping-environment-permit.name-placeholder')} />
              </SearchItem>
              <SearchItem
                name="wasteGroup"
                label={t('masterdata.waste-grouping-environment-permit.group')}
                rules={[{ required: true, message: t('masterdata.waste-grouping-environment-permit.group-required') }]}
              >
                <CustomSelect
                  options={wasteGroupOptions}
                  placeholder={t('masterdata.waste-grouping-environment-permit.group-placeholder')}
                  showSearch
                  optionFilterProp="label"
                  filterOption={customFilterOption}
                  allowClear
                />
              </SearchItem>
              <SearchItem name="quantity" label={t('masterdata.waste-grouping-environment-permit.volume')}>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder={t('masterdata.waste-grouping-environment-permit.volume-placeholder')}
                  min={0}
                  precision={3}
                  controls={false}
                  formatter={(value: string | number | undefined, info) => {
                    if (value === undefined || value === null || value === '') return '';
                    // Khi đang gõ: chỉ format dấu phẩy, không thêm .000 để giữ con trỏ ở đúng vị trí
                    if (info?.userTyping) {
                      return sFormatNumber(Number(value)) as string;
                    }
                    // Khi blur hoặc hiển thị: format đầy đủ với .000
                    return formatDecimalNumber(`${value}`);
                  }}
                  parser={(value: string | undefined) => {
                    if (!value) return 0;
                    const numberWithoutCommas = value.replace(/,/g, '');
                    return parseFloat(numberWithoutCommas) || 0;
                  }}
                />
              </SearchItem>
              <SearchItem name="unit" label={t('masterdata.waste-grouping-environment-permit.unit')}>
                <CustomSelect
                  options={unitOptions}
                  placeholder={t('masterdata.waste-grouping-environment-permit.unit-placeholder')}
                  showSearch
                  optionFilterProp="label"
                  filterOption={customFilterOption}
                  allowClear
                />
              </SearchItem>
              <SearchItem name="year" label={t('masterdata.waste-grouping-environment-permit.year')}>
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder={t('masterdata.waste-grouping-environment-permit.year-placeholder')}
                  min={1900}
                  max={2100}
                  precision={0}
                  controls={false}
                />
              </SearchItem>
              <SearchItem
                name="effectiveDate"
                label={t('masterdata.waste-grouping-environment-permit.effective-date')}
              >
                <DatePicker
                  className="w-full"
                  format="DD/MM/YYYY"
                  placeholder={t('masterdata.waste-grouping-environment-permit.effective-date-placeholder')}
                />
              </SearchItem>
              <SearchItem name="isDisplayCodeInReport" label={t('masterdata.waste-grouping-environment-permit.is-display-code-in-report')}>
                <Radio.Group className="flex gap-6">
                  <Radio value={true}>{t('radio-btn.yes')}</Radio>
                  <Radio value={false}>{t('radio-btn.no')}</Radio>
                </Radio.Group>
              </SearchItem>
              {id && (
                <SearchItem name="actived" label={t('masterdata.waste-grouping-environment-permit.status')}>
                  <Radio.Group className="flex gap-6">
                    <Radio value={true}>{t('radio-btn.active')}</Radio>
                    <Radio value={false}>{t('radio-btn.in-active')}</Radio>
                  </Radio.Group>
                </SearchItem>
              )}
            </SearchForm>
          </Spin>
        </BoxContainer>
        {id ? (
          <BoxContainer>
            <CustomProTable
              headerTitle={<span className="font-semibold">{t('masterdata.waste-grouping-environment-permit.history-title')}</span>}
              columns={[
                { title: t('masterdata.waste-grouping-environment-permit.stt'), dataIndex: 'stt', width: 50, align: 'center', render: (_, __, index) => index + 1 },
                {
                  title: t('masterdata.waste-grouping-environment-permit.volume'),
                  dataIndex: 'oldQuantity',
                  width: 80,
                  align: 'right',
                  render: (value: any) => value != null && value !== '' ? formatDecimalNumber(`${value}`) : '-'
                },
                { title: t('masterdata.waste-grouping-environment-permit.unit'), dataIndex: 'oldUnitName', width: 70, align: 'center' },
                { title: t('masterdata.waste-grouping-environment-permit.year'), dataIndex: 'oldYear', width: 70, align: 'center' },
                {
                  title: t('masterdata.waste-grouping-environment-permit.effective-date'),
                  dataIndex: 'oldEffectiveDate',
                  width: 100,
                  align: 'center',
                  valueType: 'dateTime',
                  fieldProps: {
                    format: DATE_FORMAT_DEFAULT,
                  },
                },
              ]}
              dataSource={historyData.map((item, index) => ({
                ...item,
                stt: index + 1,
              }))}
              pagination={false}
              rowKey="wasteGroupingEnvironmentPermitLogId"
              search={false}
              options={false}
              scroll={{ x: 370 }}
            />
          </BoxContainer>
        ) : null}
      </div>
    );
  });
};

export default createWasteGroupingEnvironmentPermitDetailPage;

